import { ClipboardDocumentListIcon } from '@heroicons/react/24/solid';
import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { showToast } from '../Notification/ToastrNotif.module';

interface Subtitle {
  label: string;
  value: string | number;
}

interface BaseKPICardProps {
  icon: React.ReactNode;
  title: string;
  subtitles?: Subtitle[];
  color?: string;
  loading?: boolean;
  copy?: boolean;
  onClick?: () => void;
}

// Forcing either value or children to be provided
interface ValueKPICardProps extends BaseKPICardProps {
  value: string | number;
  children?: React.ReactNode;
}

interface ChildrenKPICardProps extends BaseKPICardProps {
  value?: never;
  children: React.ReactNode;
}

type KPICardProps = ValueKPICardProps | ChildrenKPICardProps;

function KPICard({
  icon,
  title,
  value,
  subtitles = [],
  color = 'text-rgpd-blue-dark',
  loading = false,
  copy = false,
  onClick,
  children,
}: KPICardProps) {
  return (
    <div
      className={`rounded-lg bg-gray-100 p-4 transition-all duration-200 ${
        onClick
          ? 'transform cursor-pointer hover:-translate-y-1 hover:bg-gray-200 hover:shadow-md active:translate-y-0 active:bg-gray-300 active:shadow-inner'
          : ''
      }`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-start">
        <div className={`${color} mr-4 transition-transform duration-200 ${onClick ? 'group-hover:scale-110' : ''}`}>
          {icon}
        </div>
        <div className="grow">
          <h3 className="text-sm font-medium text-gray-500">{title}</h3>
          {loading ? (
            <Skeleton width={80} />
          ) : (
            <>
              <p className="flex items-center justify-between text-lg font-semibold">
                <span>{value}</span>
                {copy && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(value.toString());
                      showToast('success', 'Copié dans le presse-papiers.');
                    }}
                    className="ml-1 rounded-full border border-gray-300 px-2 py-1 text-sm text-gray-500 transition-colors hover:border-rgpd-blue-dark hover:text-rgpd-blue-dark focus:ring-2 focus:ring-rgpd-blue-dark focus:outline-none"
                    aria-label="Copier"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4" />
                  </button>
                )}
              </p>
              {subtitles && subtitles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {subtitles.map((subtitle, index) => (
                    <p key={index} className="text-xs text-gray-500">
                      {subtitle.label} :{' '}
                      <span className="font-medium">{subtitle.value}</span>
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {onClick && (
          <div className="ml-2 self-center text-lg text-gray-500 transition-transform duration-200 group-hover:translate-x-1">
            →
          </div>
        )}
      </div>
      {children && <div className="mt-2 text-xs">{children}</div>}
    </div>
  );
}

export default KPICard;
